/**
 * `upload-bundle-plan` / `upload-bundle-execute` — CLM-57, FR-13, FR-14, IR-01.
 *
 * Several signed MSAs/SOWs filed at once: one PDF or one ZIP of PDFs is posted
 * as the `document` part, the server **plans** (expands the archive, hashes and
 * detects each entry, flags duplicates) and the reviewer approves rows which the
 * server then **executes** through the very same executor the guided upload
 * uses — one `clm.contract` row, one `Create` audit row and one ledger row per
 * document, per-document transactions.
 *
 * ## FR-14 by absence, again
 *
 * Neither request carries a `contract_key` or a `sequence_number`. Each approved
 * row names a `primary_party_key` the plan offered (selectable parties, scoped);
 * the key (`PartyKey-nnnn`, D-110-1) is minted per row under the same per-party
 * lock as `upload`. Both metadata schemas are `.strict()`, so a `contract_key`
 * in either is a `400` from the schema.
 *
 * ## Nobody picks a type (CLM-118, D-110-3)
 *
 * A bundle is an upload, and nobody filing it is asked for a type (B9): no row
 * type, no ZIP-wide default, no type list. The server types each row from its
 * path by the deterministic file-name rule — folder first (CLM-57), a type word
 * or a SOW-shaped number — and records the evidence (`name_evidence`). A row the
 * name gives no type is filed **Type pending** (`needs_type`, never blocking); a
 * row whose name contradicts itself is pending too (`type_evidence_conflicting`,
 * never blocking). A number in a row's SOW-number cell files it as a SOW, never
 * as a trusted file-name type (R-118-19). The AI's reading then confirms or
 * supplies the type for an Admin to verify.
 *
 * ## The plan is not persisted (rule 8)
 *
 * The bundle is re-sent on execute and re-expanded; `bundle_sha256` and each
 * approved row's `sha256` are re-verified against what was re-sent, and any
 * disagreement is a `409` with nothing filed. No staging store, no DDL.
 */
import { z } from 'zod';
import { ContractKeySchema, PartyKeySchema, Sha256Schema } from '../primitives.js';
import { AmendmentSignalNameSchema, ContractTypeConfirmationSchema, ContractTypeNameEvidenceSchema } from '../vocabulary.js';
import { SelectablePartySchema, UploadResponseSchema } from './upload.js';
import { TAGS_PER_CONTRACT_MAX, TagSchema } from './contract-tags.js';
/* -------------------------------------------------------------------------- */
/* Vocabularies                                                               */
/* -------------------------------------------------------------------------- */
/**
 * Types a bundle row may be filed as — the ingest subset, D-76-1 (CLM-76). The
 * server types each row (CLM-118); `OTHER` is only ever Type pending's
 * placeholder, never detected (D-76-2).
 */
export const BUNDLE_CONTRACT_TYPES = ['MSA', 'SOW', 'NDA', 'OTHER'];
export const BundleContractTypeSchema = z.enum(BUNDLE_CONTRACT_TYPES);
export const BUNDLE_ROW_DETECTED_TYPES = BUNDLE_CONTRACT_TYPES;
/**
 * CLM-76 (D-76-3), CLM-118 (R-118-5). How a row's `detected_type` was arrived at:
 * a `folder` or `file_name` token (folder-first, CLM-57), or `sow_number` — no
 * token but a **SOW-shaped** candidate in the path (the confidence flag). The
 * bundle default is retired with the type question (R-118-18).
 */
export const BUNDLE_TYPE_MATCHES = ['folder', 'file_name', 'sow_number'];
export const BundleTypeMatchSchema = z.enum(BUNDLE_TYPE_MATCHES);
/** Per-row flags the plan raises. Text + icon in the UI, never colour alone. */
export const BUNDLE_ROW_FLAGS = [
    'duplicate_in_bundle',
    'duplicate_existing',
    'needs_party',
    /**
     * CLM-114 · R-114-4 — a folder segment or file-name token names two or more
     * parties (display name or active alias): no party is detected and a person
     * chooses. Raised beside `needs_party`; replaces the silent "last wins".
     */
    'ambiguous_party',
    /** CLM-110 (R-110-27), CLM-118 — informational, never blocking: the path gives no type, the row files Type pending, and the AI reads the document. */
    'needs_type',
    /** D-71-0, CLM-118 (R-118-19) — a SOW row with no SOW-shaped (`dominant_shape` / `sow_infix`) usable `sow_candidates` entry, so its number cell starts empty (a year or a bare number is only suggested): the review asks for the number in the row's cell. Never blocking in the plan; execute refuses a SOW row sent with none (`sow_number_required`). */
    'needs_sow_number',
    'restricted',
    /** CLM-118 (R-118-5) — informational, never blocking: the path names more than one type, or a type word beside a SOW number, so the row files Type pending. */
    'type_evidence_conflicting',
];
export const BundleRowFlagSchema = z.enum(BUNDLE_ROW_FLAGS);
/**
 * Why an archive entry is listed but never filed. A per-entry exclusion, unlike
 * the whole-bundle guard failures (`encrypted_entry`, `nested_archive`, …) which
 * refuse the request outright.
 */
export const BUNDLE_EXCLUSION_REASONS = ['not_pdf', 'directory', 'system_file'];
export const BundleExclusionReasonSchema = z.enum(BUNDLE_EXCLUSION_REASONS);
/** Whole-bundle guard failures — `field_errors[0].message` on a 400/413 plan. */
export const BUNDLE_GUARD_FAILURES = [
    'not_pdf_or_zip',
    'too_many_entries',
    'entry_too_large',
    'total_too_large',
    'inflate_ratio',
    'nested_archive',
    'encrypted_entry',
    'undecodable_entry',
    'unsafe_entry_path',
];
export const BUNDLE_ROW_OUTCOMES = [
    'uploaded',
    'duplicate',
    'conflict',
    'rejected',
    'failed',
    'skipped_parent_not_filed',
];
export const BundleRowOutcomeSchema = z.enum(BUNDLE_ROW_OUTCOMES);
/** CLM-126 — `sow_prefix` added: the row's party came from the SOW-number prefix in the file name (CLM-88, ported to ZIP). */
export const BUNDLE_PARTY_MATCHES = ['folder', 'file_name', 'default', 'sow_prefix'];
/**
 * CLM-126 — what the client claims produced an approved row's `primary_party_key`,
 * so the server can credit the right audit actor (R-126-11, R-126-17). Optional
 * on the wire: an older web omits it and the row is attributed to the ordinary
 * default actor, exactly as before this story. The server never trusts this
 * claim outright — it is honoured only when re-verified against the server's
 * own re-derivation (R-126-17 (2)).
 */
export const BUNDLE_PARTY_KEY_SOURCES = ['folder', 'file_name', 'sow_prefix', 'default', 'nearest_match', 'manual'];
export const BundlePartyKeySourceSchema = z.enum(BUNDLE_PARTY_KEY_SOURCES);
/* -------------------------------------------------------------------------- */
/* The bundle descriptor                                                      */
/* -------------------------------------------------------------------------- */
export const BundleMediaTypeSchema = z.enum(['application/pdf', 'application/zip']);
/**
 * Descriptor of the `document` part — one PDF or one ZIP. As
 * `UploadDocumentDescriptorSchema`, the bytes travel as a multipart part and the
 * descriptor is verified server-side against them.
 */
export const BundleDescriptorSchema = z
    .object({
    file_name: z.string().min(1).max(255),
    media_type: BundleMediaTypeSchema,
    size_bytes: z.number().int().min(1),
    sha256: Sha256Schema,
})
    .strict();
/* -------------------------------------------------------------------------- */
/* Plan                                                                        */
/* -------------------------------------------------------------------------- */
export const UploadBundlePlanRequestSchema = z
    .object({
    bundle: BundleDescriptorSchema,
    /** Applied to rows with no detected party. Must be a selectable party. */
    default_party_key: PartyKeySchema.optional(),
    /*
     * CLM-78 (B6) — the counterparty kind is NOT a plan field. `parties` carries
     * every selectable party with its `party_type`; the review screen seeds each
     * row's kind from the detected party's type (or its local default) and swaps
     * the picker client-side. Nothing about the kind is stored (K5).
     */
})
    .strict();
export const BundleSowCandidateSchema = z.object({
    value: z.string().min(1).max(64),
    shape: z.string().min(1).max(40),
    usable_as_lookup_key: z.boolean(),
});
export const BundleRelationshipKindSchema = z.enum(['standalone', 'child_of', 'amends', 'supersedes']);
/**
 * A parent the plan may offer, with the §8.1 relationships it is valid for
 * **per bundle type** — a bundle mixes MSA and SOW rows, so one list per row
 * type is what lets the review filter without re-deriving the rule client-side.
 * `valid_for` is keyed by the type the **server** gives a row (CLM-118);
 * `valid_for_untyped` serves a Type pending row.
 */
export const BundleCandidateParentSchema = z.object({
    contract_key: ContractKeySchema,
    display_label: z.string().min(1).max(300),
    contract_type: z.string().min(1).max(12),
    /** CLM-110 (R-110-23) — rendered beside `contract_type` as its `provisional` tag. */
    contract_type_confirmation: ContractTypeConfirmationSchema,
    valid_for: z.record(BundleContractTypeSchema, z.array(z.enum(['child_of', 'amends', 'supersedes']))),
    /**
     * CLM-110 (R-110-27) — the relationships this candidate is valid for from a row
     * with **no** type: every one its own type and lifecycle permit
     * (`permittedRelationships(candidate, null, lifecycle)`), with `amends` withheld
     * unless the row's party is the candidate's primary party.
     */
    valid_for_untyped: z.array(z.enum(['child_of', 'amends', 'supersedes'])),
});
export const BundlePlanRowSchema = z.object({
    /** `r1`, `r2`, … in archive order. Stable between plan and execute. */
    row_id: z.string().regex(/^r[1-9]\d*$/),
    /** The entry's path inside the archive — a display string, never a filesystem path. */
    path: z.string().min(1).max(1024),
    file_name: z.string().min(1).max(255),
    size_bytes: z.number().int().min(1),
    sha256: Sha256Schema,
    /** CLM-118 — the type the server files the row under; `null` for a Type pending row (untyped or conflicting). */
    detected_type: BundleContractTypeSchema.nullable(),
    /** CLM-76. `null` iff `detected_type` is `null`. */
    type_match: BundleTypeMatchSchema.nullable(),
    /** CLM-118 (R-118-3, R-118-5) — what the deterministic file-name rule found in the row's path. */
    name_evidence: ContractTypeNameEvidenceSchema,
    /** CLM-118 (R-118-14) — the amendment word in the path, if any (a flag for the Admin; the type ignores it). */
    amendment_signal: AmendmentSignalNameSchema.nullable(),
    /** Only ever a key from `parties` — never a guess (B-24). */
    detected_party_key: PartyKeySchema.nullable(),
    party_match: z.enum(BUNDLE_PARTY_MATCHES).nullable(),
    sow_candidates: z.array(BundleSowCandidateSchema),
    /**
     * CLM-126 — up to 3 nearest-match candidates, trigram-scored, offered only
     * when exact resolution left this row `needs_party` or `ambiguous_party`
     * (R-126-15). A **suggestion only** (FR-14 extended, never bypassed): empty
     * when exact resolution already succeeded, or when nothing cleared
     * `PARTY_MATCH_FLOOR`.
     */
    nearest_matches: z.array(SelectablePartySchema).max(3),
    /** §4C — inherited from the detected/default party. */
    processing_restricted: z.boolean(),
    flags: z.array(BundleRowFlagSchema),
    duplicate_of_row_id: z.string().nullable(),
    /** `null` when the holder is out of scope — same rule as `UploadOutcome.duplicate`. */
    existing_contract_key: ContractKeySchema.nullable(),
    valid_relationships: z.array(BundleRelationshipKindSchema),
});
export const BundleExcludedEntrySchema = z.object({
    path: z.string().min(1).max(1024),
    reason: BundleExclusionReasonSchema,
});
export const UploadBundlePlanResponseSchema = z.object({
    bundle_sha256: Sha256Schema,
    entry_count: z.number().int().min(0),
    rows: z.array(BundlePlanRowSchema),
    excluded: z.array(BundleExcludedEntrySchema),
    totals: z.object({
        ready: z.number().int().min(0),
        needs_party: z.number().int().min(0),
        /** CLM-110 (R-110-27) — "No type yet": counted, but such a row is not blocked and counts toward `ready`. */
        needs_type: z.number().int().min(0),
        /** CLM-118 (R-118-19) — counted, never blocking: the number is entered in the row's cell. */
        needs_sow_number: z.number().int().min(0),
        /** CLM-118 (R-118-5) — counted, never blocking: the row files Type pending. */
        type_evidence_conflicting: z.number().int().min(0),
        duplicate_in_bundle: z.number().int().min(0),
        duplicate_existing: z.number().int().min(0),
        restricted: z.number().int().min(0),
        excluded: z.number().int().min(0),
    }),
    /** The only source of `party_key` the review may offer (FR-14). */
    parties: z.array(SelectablePartySchema),
    candidate_parents_by_party: z.record(PartyKeySchema, z.array(BundleCandidateParentSchema)),
    max_document_bytes: z.number().int().min(1),
    max_bundle_bytes: z.number().int().min(1),
    /** Informational: more rows than the ~10/month design volume (§0.1). */
    exceeds_design_volume: z.boolean(),
});
/* -------------------------------------------------------------------------- */
/* Execute                                                                     */
/* -------------------------------------------------------------------------- */
/**
 * A row's relationship: the `upload` union, plus `child_of` an MSA **in this
 * bundle** by `parent_row_id` — the key does not exist until that row is filed.
 */
export const BundleRelationshipSchema = z.discriminatedUnion('relationship', [
    z.object({ relationship: z.literal('standalone') }).strict(),
    z
        .object({
        relationship: z.literal('child_of'),
        target_contract_key: ContractKeySchema.optional(),
        parent_row_id: z.string().regex(/^r[1-9]\d*$/).optional(),
    })
        .strict()
        .refine((value) => (value.target_contract_key === undefined) !== (value.parent_row_id === undefined), { message: 'child_of names exactly one of target_contract_key or parent_row_id' }),
    z.object({ relationship: z.literal('amends'), target_contract_key: ContractKeySchema }).strict(),
    z
        .object({ relationship: z.literal('supersedes'), target_contract_key: ContractKeySchema })
        .strict(),
]);
export const BundleApprovedRowSchema = z
    .object({
    row_id: z.string().regex(/^r[1-9]\d*$/),
    /** Re-verified against the re-sent bundle; mismatch is a whole-request 409. */
    sha256: Sha256Schema,
    /* Selected from `parties`, never typed — FR-14. No type: the server types the row from its path (CLM-118, B9). */
    primary_party_key: PartyKeySchema,
    relationship: BundleRelationshipSchema,
    /**
     * IR-01 — typed or accepted from `sow_candidates`; never minted (CLM-118,
     * R-118-19). Required when the server types the row `SOW` (D-71-0, refused
     * per row as `sow_number_required`); on a row the path gives no type, a
     * number files it as a SOW, never a trusted file-name type.
     */
    sow_number: z.string().min(1).max(32).optional(),
    processing_restricted_override: z.literal(true).optional(),
    processing_restriction_reason: z.string().min(1).max(1000).optional(),
    /** CLM-77 — per-row tags (seeded from the review screen's default, D-77-6); passed through to `executeUpload`. */
    tags: z.array(TagSchema).max(TAGS_PER_CONTRACT_MAX).optional(),
    /**
     * CLM-126 (R-126-11, R-126-17) — what the plan's own evidence named for
     * this row, seeded client-side for **every** row (not only an accepted
     * suggestion), and reset to `'manual'` on any free pick. Optional: an
     * older web omits it and the row is attributed to today's default actor.
     * Verified server-side before it is trusted (R-126-17 (2)) — never taken
     * at face value.
     */
    party_key_source: BundlePartyKeySourceSchema.optional(),
    /**
     * CLM-126 — present only when `party_key_source` is `'nearest_match'`:
     * which offered candidate the reviewer accepted, and at what score. The
     * server honours it only when `party_key` is present among the row's own
     * candidate set **and** equals `primary_party_key` (R-126-12).
     */
    suggestion_accepted: z.object({ party_key: PartyKeySchema, score: z.number().min(0).max(1) }).optional(),
})
    .strict();
export const UploadBundleExecuteRequestSchema = z
    .object({
    bundle: BundleDescriptorSchema,
    bundle_sha256: Sha256Schema,
    approved: z.array(BundleApprovedRowSchema).min(1),
})
    .strict();
export const BundleRowResultSchema = z.object({
    row_id: z.string().regex(/^r[1-9]\d*$/),
    outcome: BundleRowOutcomeSchema,
    /** When `uploaded` — the contract's own `UploadResponse`, receipt included. */
    upload: UploadResponseSchema.optional(),
    /** When `duplicate` — `null` if the holder is out of scope. */
    contract_key: ContractKeySchema.nullable().optional(),
    reason: z.string().max(80).optional(),
    detail: z.string().max(2000).optional(),
    http_equivalent: z.union([z.literal(409), z.literal(400), z.literal(413), z.literal(415), z.literal(422), z.literal(500)]).optional(),
});
export const UploadBundleExecuteResponseSchema = z.object({
    bundle_sha256: Sha256Schema,
    results: z.array(BundleRowResultSchema),
    totals: z.object({
        uploaded: z.number().int().min(0),
        duplicate: z.number().int().min(0),
        conflict: z.number().int().min(0),
        rejected: z.number().int().min(0),
        failed: z.number().int().min(0),
        skipped: z.number().int().min(0),
    }),
});
//# sourceMappingURL=upload-bundle.js.map